"use client";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { Layers } from "lucide-react";
import { AsyncBoundary, EmptyState, Skeleton } from "@gerama/ui";
import { browserCoreApi } from "@/lib/api";
import { categoryColor } from "@/features/discover/category-color";
import { CategoryArt } from "@/features/discover/category-art";
import { InfiniteSentinel, useInfiniteList } from "@/features/discover/use-infinite-list";
import { SongList, SongListHeader, useSongView } from "@/features/discover/song-list-view";
import { CardGrid, Shelf } from "@/features/discover/shelf";
import { VideoCard } from "@/features/discover/video-card";
import { useCategoryChildren, useCategoryDetail } from "./use-categories";

export function CategoryBrowse({ id }: { id: number }) {
  const detailQuery = useCategoryDetail(id);

  return (
    <AsyncBoundary
      variant="immersive"
      query={detailQuery}
      skeleton={<Skeleton className="h-40 w-full rounded-xl" />}
      isEmpty={() => false}
      empty={
        <EmptyState
          variant="immersive"
          icon={<Layers className="size-8" />}
          title="دسته‌بندی یافت نشد"
        />
      }
      render={(category) => (
        <CategoryView
          id={id}
          name={category.nameLocal.fa ?? category.name}
          color={categoryColor(category)}
          image={category.image}
        />
      )}
    />
  );
}

function CategoryView({
  id,
  name,
  color,
  image,
}: {
  id: number;
  name: string;
  color: string;
  image: string | null;
}) {
  const children = useCategoryChildren(id);
  const [view, setView] = useSongView();
  const songs = useInfiniteList({
    queryKey: ["categories", id, "songs"],
    fetchPage: (page, limit, signal) =>
      browserCoreApi.songs.list({ categoryId: id, page, limit, sort: "newest" }, signal),
    limit: 30,
  });
  const videos = useQuery({
    queryKey: ["categories", id, "videos"],
    queryFn: ({ signal }) =>
      browserCoreApi.videos.list({ categoryId: id, limit: 3, sort: "trending" }, signal),
    staleTime: 60_000,
  });
  const subcategories = children.data?.list ?? [];

  return (
    <div className="space-y-10">
      <header
        className="relative isolate -mx-4 -mt-6 overflow-hidden px-4 pt-14 pb-8 sm:mx-0 sm:mt-0 sm:rounded-xl sm:px-8"
        style={{
          background: `linear-gradient(135deg, ${color} 0%, color-mix(in oklab, ${color} 40%, black) 100%)`,
        }}
      >
        <CategoryArt
          category={{ id, name, image }}
          sizes="224px"
          className="-end-6 -bottom-6 -z-10 size-48 rounded-2xl opacity-90 sm:size-56"
        />
        <p className="text-sm font-medium text-white/80">دسته‌بندی</p>
        <h1 className="text-4xl font-black text-white drop-shadow sm:text-6xl">{name}</h1>
        {subcategories.length > 0 && (
          <ul className="mt-6 flex flex-wrap gap-2">
            {subcategories.map((sub) => (
              <li key={sub.id}>
                <Link
                  href={`/categories/${sub.id}`}
                  className="rounded-full bg-white/15 px-4 py-1.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/25"
                >
                  {sub.nameLocal.fa ?? sub.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </header>

      {(videos.data?.list.length ?? 0) > 0 && (
        <Shelf title="نماهنگ‌ها" href={`/videos`}>
          <CardGrid columns="video">
            {videos.data!.list.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                contextLabel={name}
                contextHref={`/categories/${id}`}
              />
            ))}
          </CardGrid>
        </Shelf>
      )}

      <section className="space-y-4">
        <SongListHeader title="آهنگ‌ها" view={view} onView={setView} level="h2" />
        {songs.isPending ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : songs.items.length === 0 ? (
          <EmptyState variant="immersive" title="آهنگی در این دسته نیست" />
        ) : (
          <>
            <SongList
              songs={songs.items}
              view={view}
              contextLabel={name}
              contextHref={`/categories/${id}`}
            />
            <InfiniteSentinel
              hasNext={!!songs.hasNextPage}
              loading={songs.isFetchingNextPage}
              onLoadMore={() => void songs.fetchNextPage()}
            />
          </>
        )}
      </section>
    </div>
  );
}
